/**
 * Business Hours Page - Tailwind Version
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Clock,
  Save,
  RefreshCw,
  Copy,
  Info,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { BusinessHourRestaurant } from "@tasty/api";
import { BusinessHourRestaurantClient } from "../../api-client";
import { useRestaurantContext } from "../../context/restaurant-context";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../../components/ui/card";
import { Switch } from "../../components/ui/switch";
import { cn } from "../../lib/utils";
import { toast } from "sonner";

const DAYS = [
  { day: 1, key: "monday", label: "Montag" },
  { day: 2, key: "tuesday", label: "Dienstag" },
  { day: 3, key: "wednesday", label: "Mittwoch" },
  { day: 4, key: "thursday", label: "Donnerstag" },
  { day: 5, key: "friday", label: "Freitag" },
  { day: 6, key: "saturday", label: "Samstag" },
  { day: 7, key: "sunday", label: "Sonntag" },
];

type DayHours = { open: string; close: string; isOpen: boolean };
type WeekSchedule = Record<string, DayHours>;
const DEFAULT_HOURS: DayHours = {
  open: "11:00",
  close: "22:00",
  isOpen: false,
};

export default function BusinessHoursPage() {
  const { restaurant } = useRestaurantContext();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [businessHours, setBusinessHours] = useState<BusinessHourRestaurant[]>(
    []
  );
  const [schedule, setSchedule] = useState<WeekSchedule>(() => {
    const initial: WeekSchedule = {};
    DAYS.forEach((day) => (initial[day.key] = { ...DEFAULT_HOURS }));
    return initial;
  });

  const fetchData = useCallback(async () => {
    if (!restaurant?.id) return;
    setLoading(true);
    try {
      const data = await BusinessHourRestaurantClient.queryList({
        filter: { restaurantID: { eq: restaurant.id } },
      });
      setBusinessHours(data || []);
      if (data && data.length > 0) {
        const newSchedule: WeekSchedule = {};
        DAYS.forEach((day) => {
          const dayData = data.find((bh) => bh.day === day.day);
          if (dayData) {
            const firstPeriod = dayData.periods?.[0] || null;
            newSchedule[day.key] = {
              open: firstPeriod?.start || "11:00",
              close: firstPeriod?.end || "22:00",
              isOpen: Boolean(dayData.periods && dayData.periods.length > 0),
            };
          } else {
            newSchedule[day.key] = { ...DEFAULT_HOURS };
          }
        });
        setSchedule(newSchedule);
      }
    } finally {
      setLoading(false);
    }
  }, [restaurant?.id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateDaySchedule = (
    dayKey: string,
    field: keyof DayHours,
    value: string | boolean
  ) => {
    setSchedule((prev) => ({
      ...prev,
      [dayKey]: { ...prev[dayKey], [field]: value },
    }));
  };

  const handleSave = async () => {
    if (!restaurant?.id) return;
    setSaving(true);
    try {
      await Promise.all(
        DAYS.map(async (day) => {
          const daySchedule = schedule[day.key];
          const existing = businessHours.find((bh) => bh.day === day.day);
          if (daySchedule.isOpen && daySchedule.open >= daySchedule.close)
            throw new Error(
              `${day.label}: Öffnungszeit muss vor Schließzeit liegen.`
            );
          const periods = !daySchedule.isOpen
            ? []
            : [
                {
                  label: "Standard",
                  start: daySchedule.open,
                  end: daySchedule.close,
                },
              ];
          if (existing) {
            await BusinessHourRestaurantClient.updateItem({
              id: existing.id,
              restaurantID: restaurant.id,
              day: day.day,
              periods,
            });
          } else if (daySchedule.isOpen) {
            await BusinessHourRestaurantClient.createItem({
              restaurantID: restaurant.id,
              day: day.day,
              periods,
            });
          }
        })
      );
      toast.success("Gespeichert");
      fetchData();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const isStoreCurrentlyOpen = useMemo(() => {
    const now = new Date();
    const dayOfWeek = now.getDay() === 0 ? 7 : now.getDay();
    const currentTime = `${String(now.getHours()).padStart(2, "0")}:${String(
      now.getMinutes()
    ).padStart(2, "0")}`;
    const todayKey = DAYS.find((d) => d.day === dayOfWeek)?.key;
    if (!todayKey) return false;
    const h = schedule[todayKey];
    return h.isOpen && currentTime >= h.open && currentTime <= h.close;
  }, [schedule]);

  return (
    <div className="w-full space-y-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b pb-4">
        <div className="space-y-1">
          <h1 className="text-3xl font-black tracking-tight">Holiday Hours</h1>
          <p className="text-sm font-medium text-muted-foreground">
            Verwalte die Betriebszeiten deines Restaurants.
          </p>
        </div>
        <div className="flex items-center gap-3 mb-2">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="h-10 px-8 rounded-xl font-bold shadow-lg"
          >
            <Save className="h-4 w-4 mr-2" /> Speichern
          </Button>
        </div>
      </div>

      <Card
        className={cn(
          "border-none shadow-sm",
          isStoreCurrentlyOpen ? "bg-success/5" : "bg-muted/30"
        )}
      >
        <CardContent className="p-6 flex items-center gap-4">
          <div
            className={cn(
              "h-12 w-12 rounded-full flex items-center justify-center shadow-inner",
              isStoreCurrentlyOpen
                ? "bg-success text-success-foreground"
                : "bg-muted-foreground/20 text-muted-foreground"
            )}
          >
            {isStoreCurrentlyOpen ? (
              <CheckCircle2 className="h-6 w-6" />
            ) : (
              <XCircle className="h-6 w-6" />
            )}
          </div>
          <h3 className="text-xl font-bold">
            {isStoreCurrentlyOpen ? "Geöffnet" : "Geschlossen"}
          </h3>
        </CardContent>
      </Card>

      <Card className="shadow-sm border-muted-foreground/10 overflow-hidden rounded-2xl">
        <div className="divide-y divide-muted/60">
          {DAYS.map((day) => (
            <div
              key={day.key}
              className={cn(
                "p-6 flex flex-col md:flex-row md:items-center gap-6",
                !schedule[day.key].isOpen && "bg-muted/5 opacity-70"
              )}
            >
              <span className="w-32 font-bold">{day.label}</span>
              <Switch
                checked={schedule[day.key].isOpen}
                onCheckedChange={(v) => updateDaySchedule(day.key, "isOpen", v)}
              />
              <div className="flex items-center gap-2">
                <Input
                  type="time"
                  value={schedule[day.key].open}
                  onChange={(e) =>
                    updateDaySchedule(day.key, "open", e.target.value)
                  }
                  disabled={!schedule[day.key].isOpen}
                  className="w-32 h-10 rounded-lg bg-muted/10 border-none font-bold"
                />
                <span className="text-[10px] font-black text-muted-foreground uppercase">
                  bis
                </span>
                <Input
                  type="time"
                  value={schedule[day.key].close}
                  onChange={(e) =>
                    updateDaySchedule(day.key, "close", e.target.value)
                  }
                  disabled={!schedule[day.key].isOpen}
                  className="w-32 h-10 rounded-lg bg-muted/10 border-none font-bold"
                />
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
